import React from "react";
import { Helmet } from "react-helmet";
import {
  Shield,
  Building2,
  Database,
  User,
  Briefcase,
  Store,
  MapPin,
  Monitor,
  Settings,
} from "lucide-react";
import "./Legal.css";

export default function PrivacyPolicy() {
  return (
    <>
      <Helmet>
        <title>
          Privacy Policy | Civilink Private Limited
        </title>

        <meta
          name="description"
          content="Read the official Privacy Policy of Civilink Private Limited to understand how we collect, use, protect and manage your personal information while using our construction marketplace."
        />

        <link
          rel="canonical"
          href="https://www.civilink.in/privacy-policy"
        />

        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Privacy Policy | Civilink Private Limited"
        />
        <meta
          property="og:description"
          content="Official Privacy Policy of Civilink Private Limited."
        />
        <meta
          property="og:url"
          content="https://www.civilink.in/privacy-policy"
        />
      </Helmet>

      <div className="legal-page">

        {/* Hero */}

        <section className="legal-hero">

          <Shield size={60} />

          <h1>Privacy Policy</h1>

          <p>
            Civilink Private Limited
          </p>

          <span>
            Last Updated: July 29, 2026
          </span>

        </section>

        {/* Introduction */}

        <section className="legal-card">

          <div className="legal-title">

            <Building2 size={26} />

            <h2>1. Introduction</h2>

          </div>

          <p>

            Welcome to <strong>Civilink Private Limited</strong>
            ("Civilink", "Company", "we", "our", or "us").

          </p>

          <p>

            Civilink is committed to protecting your privacy and
            handling your personal information responsibly.

          </p>

          <p>

            This Privacy Policy explains how we collect,
            use,
            store,
            disclose,
            and protect your information when you use
            the Civilink website,
            mobile applications,
            marketplace,
            and related services.

          </p>

          <p>

            By accessing or using the Platform,
            you acknowledge that you have read and
            understood this Privacy Policy.

          </p>

        </section>

        {/* Platform */}

        <section className="legal-card">

          <div className="legal-title">

            <Building2 size={26} />

            <h2>2. About Civilink</h2>

          </div>

          <p>

            Civilink Private Limited is an Indian
            Construction Technology (ConTech)
            company operating a digital construction
            marketplace.

          </p>

          <p>

            Our platform connects:

          </p>

          <ul>

            <li>Homeowners</li>

            <li>Engineers</li>

            <li>Architects</li>

            <li>Contractors</li>

            <li>Interior Designers</li>

            <li>Suppliers</li>

            <li>Vendors</li>

            <li>Equipment Rental Providers</li>

            <li>Property Buyers & Sellers</li>

          </ul>

          <p>

            Civilink provides technology services
            that enable users to discover,
            communicate,
            and transact with one another.

          </p>

          <p>

            Unless specifically stated otherwise,
            Civilink does not manufacture,
            own,
            inspect,
            sell,
            deliver,
            or guarantee products or services listed
            on the Platform.

          </p>

        </section>

        {/* Scope */}

        <section className="legal-card">

          <div className="legal-title">

            <Shield size={26} />

            <h2>3. Scope of this Privacy Policy</h2>

          </div>

          <p>

            This Privacy Policy applies to information
            collected through:

          </p>

          <ul>

            <li>Civilink Website</li>

            <li>Mobile Applications</li>

            <li>Marketplace</li>

            <li>Vendor Portal</li>

            <li>Professional Profiles</li>

            <li>Customer Support</li>

            <li>Marketing Communications</li>

            <li>Business Interactions</li>

          </ul>

        </section>

        {/* Personal Information */}

        <section className="legal-card">

          <div className="legal-title">

            <User size={26} />

            <h2>4. Personal Information We Collect</h2>

          </div>

          <ul>

            <li>Full Name</li>

            <li>Email Address</li>

            <li>Mobile Number</li>

            <li>Profile Photo</li>

            <li>Date of Birth (where applicable)</li>

            <li>Postal Address</li>

            <li>City</li>

            <li>State</li>

            <li>Country</li>

          </ul>

        </section>

        {/* Professional */}

        <section className="legal-card">

          <div className="legal-title">

            <Briefcase size={26} />

            <h2>5. Professional Information</h2>

          </div>

          <ul>

            <li>Profession</li>

            <li>Company Name</li>

            <li>Experience</li>

            <li>Skills</li>

            <li>Portfolio</li>

            <li>Educational Details</li>

            <li>Licenses</li>

            <li>Certifications</li>

            <li>Office Address</li>

            <li>Service Areas</li>

          </ul>

        </section>

        {/* Vendor */}

        <section className="legal-card">

          <div className="legal-title">

            <Store size={26} />

            <h2>6. Vendor Information</h2>

          </div>

          <ul>

            <li>Business Name</li>

            <li>Business Address</li>

            <li>GST Number (if applicable)</li>

            <li>Product Details</li>

            <li>Product Images</li>

            <li>Pricing</li>

            <li>Delivery Areas</li>

            <li>Business Documents</li>

          </ul>

        </section>

        {/* Marketplace */}

        <section className="legal-card">

          <div className="legal-title">

            <Database size={26} />

            <h2>7. Marketplace Information</h2>

          </div>

          <ul>

            <li>Product Enquiries</li>

            <li>Orders</li>

            <li>Quotations</li>

            <li>Saved Products</li>

            <li>Reviews</li>

            <li>Ratings</li>

            <li>Comments</li>

            <li>Uploaded Images</li>

            <li>Project Requirements</li>

          </ul>

        </section>

        {/* Location */}

        <section className="legal-card">

          <div className="legal-title">

            <MapPin size={26} />

            <h2>8. Location Information</h2>

          </div>

          <p>

            With your permission,
            Civilink may collect GPS,
            approximate location,
            city,
            state,
            and delivery or project
            locations to improve nearby
            vendor and professional discovery.

          </p>

        </section>

        {/* Technical */}

        <section className="legal-card">

          <div className="legal-title">

            <Monitor size={26} />

            <h2>9. Technical Information</h2>

          </div>

          <ul>

            <li>IP Address</li>

            <li>Browser Type</li>

            <li>Device Information</li>

            <li>Operating System</li>

            <li>Language</li>

            <li>Crash Reports</li>

            <li>Performance Logs</li>

          </ul>

        </section>

        {/* Usage */}

        <section className="legal-card">

          <div className="legal-title">

            <Settings size={26} />

            <h2>10. How We Use Information</h2>

          </div>

          <ul>

            <li>Create and manage accounts.</li>

            <li>Verify identities.</li>

            <li>Display marketplace listings.</li>

            <li>Provide customer support.</li>

            <li>Improve platform performance.</li>

            <li>Protect against fraud.</li>

            <li>Maintain platform security.</li>

            <li>Comply with legal obligations.</li>

            <li>Develop new platform features.</li>

          </ul>

        </section>

      
    
  


        {/* Cookies */}

        <section className="legal-card">

          <div className="legal-title">

            <Database size={26} />

            <h2>11. Cookies and Similar Technologies</h2>

          </div>

          <p>
            Civilink may use cookies, session identifiers, and similar
            technologies to improve user experience, remember your
            preferences, maintain secure login sessions, analyze platform
            performance, and enhance security.
          </p>

          <p>
            You may control or disable cookies through your browser settings.
            However, some features of the Platform may not function properly if
            cookies are disabled.
          </p>

        </section>

        {/* Data Sharing */}

        <section className="legal-card">

          <div className="legal-title">

            <Shield size={26} />

            <h2>12. Sharing of Information</h2>

          </div>

          <p>
            Civilink does not sell your personal information to third parties.
          </p>

          <p>
            We may share limited information only where necessary to operate
            the Platform or comply with applicable laws.
          </p>

          <ul>

            <li>Cloud hosting providers</li>

            <li>Authentication service providers</li>

            <li>Payment gateway providers</li>

            <li>Communication and notification providers</li>

            <li>Government authorities when legally required</li>

            <li>Professional advisors such as auditors or legal consultants</li>

          </ul>

        </section>

        {/* Marketplace Privacy */}

        <section className="legal-card">

          <div className="legal-title">

            <Store size={26} />

            <h2>13. Marketplace Privacy</h2>

          </div>

          <p>
            Civilink operates as a technology marketplace that enables users,
            vendors, suppliers, buyers, sellers, and professionals to connect
            and communicate through the Platform.
          </p>

          <p>
            Information that you voluntarily choose to publish, including
            business profiles, product listings, portfolio details, reviews,
            ratings, quotations, and public contact information, may be visible
            to other users according to the features you use.
          </p>

          <p>
            Please avoid publishing confidential or sensitive information in
            public areas of the Platform unless you intend to make that
            information publicly available.
          </p>

        </section>

        {/* Vendor Information */}

        <section className="legal-card">

          <div className="legal-title">

            <Store size={26} />

            <h2>14. Vendor and Professional Information</h2>

          </div>

          <p>
            Vendors and professionals are responsible for the information they
            choose to publish on the Platform.
          </p>

          <p>
            Civilink may review certain information submitted for verification
            purposes, but publication or verification does not guarantee that
            all information will remain complete, accurate, or up to date at
            all times.
          </p>

        </section>

        {/* Payments */}

        <section className="legal-card">

          <div className="legal-title">

            <Shield size={26} />

            <h2>15. Payment Information</h2>

          </div>

          <p>
            Payments made through Civilink may be processed by trusted
            third-party payment service providers.
          </p>

          <p>
            Civilink does not intentionally store complete debit card, credit
            card, UPI PIN, or internet banking credentials on its own servers.
          </p>

        </section>

        {/* Security */}

        <section className="legal-card">

          <div className="legal-title">

            <Shield size={26} />

            <h2>16. Data Security</h2>

          </div>

          <p>
            Civilink uses reasonable technical and organizational security
            measures to help protect personal information.
          </p>

          <ul>

            <li>HTTPS encrypted communication</li>

            <li>Secure cloud infrastructure</li>

            <li>Access controls</li>

            <li>Authentication and authorization mechanisms</li>

            <li>Security monitoring</li>

            <li>Regular security improvements</li>

          </ul>

          <p>
            Although we strive to protect your information, no method of
            electronic storage or internet transmission can be guaranteed to be
            completely secure.
          </p>

        </section>

        {/* Data Retention */}

        <section className="legal-card">

          <div className="legal-title">

            <Database size={26} />

            <h2>17. Data Retention</h2>

          </div>

          <p>
            Civilink retains personal information only for as long as
            reasonably necessary to provide services, comply with applicable
            legal obligations, resolve disputes, prevent fraud, and enforce our
            agreements.
          </p>

        </section>

        {/* User Rights */}

        <section className="legal-card">

          <div className="legal-title">

            <User size={26} />

            <h2>18. Your Rights</h2>

          </div>

          <p>
            Subject to applicable law, users may have the right to:
          </p>

          <ul>

            <li>Access their personal information</li>

            <li>Update inaccurate information</li>

            <li>Request correction of information</li>

            <li>Request deletion of eligible information</li>

            <li>Manage communication preferences</li>

          </ul>

        </section>

        {/* Account Deletion */}

        <section className="legal-card">

          <div className="legal-title">

            <User size={26} />

            <h2>19. Account Deletion</h2>

          </div>

          <p>
            Users may request deletion of their Civilink account by contacting
            our support team or using available account management features.
          </p>

          <p>
            Certain information may be retained where required by applicable
            law, regulatory obligations, fraud prevention requirements, or for
            the establishment, exercise, or defense of legal claims.
          </p>

        </section>

                {/* Children's Privacy */}

        <section className="legal-card">

          <div className="legal-title">

            <User size={26} />

            <h2>20. Children's Privacy</h2>

          </div>

          <p>

            Civilink is intended for individuals who are at least
            18 years of age or the minimum legal age required to
            enter into legally binding agreements under applicable
            laws.

          </p>

          <p>

            We do not knowingly collect personal information from
            children. If we become aware that personal information
            has been collected from a child without appropriate
            authorization, we may take reasonable steps to remove
            such information.

          </p>

        </section>

        {/* International Users */}

        <section className="legal-card">

          <div className="legal-title">

            <Building2 size={26} />

            <h2>21. International Users</h2>

          </div>

          <p>

            Civilink primarily operates in India.

          </p>

          <p>

            If users access the Platform from outside India,
            they understand that their information may be
            processed and stored in India or other jurisdictions
            where our service providers operate, subject to
            applicable laws.

          </p>

        </section>

        {/* Third Party Links */}

        <section className="legal-card">

          <div className="legal-title">

            <Shield size={26} />

            <h2>22. Third-Party Websites and Services</h2>

          </div>

          <p>

            The Platform may contain links to third-party websites,
            payment providers, social media platforms, maps,
            or other external services.

          </p>

          <p>

            Civilink does not control the privacy practices or
            content of third-party websites or services.
            Users are encouraged to review the privacy policies
            of those services before sharing personal information.

          </p>

        </section>

        {/* Platform Role */}

        <section className="legal-card">

          <div className="legal-title">

            <Store size={26} />

            <h2>23. Role of Civilink Platform</h2>

          </div>

          <p>

            Civilink Private Limited operates as a technology
            platform that enables users to discover construction
            products, vendors, professionals and related services.

          </p>

          <p>

            Civilink facilitates digital communication and
            marketplace interactions between users.

          </p>

          <p>

            Civilink does not become a party to agreements,
            negotiations or transactions entered into between
            users unless expressly stated otherwise.

          </p>

        </section>

        {/* Privacy Disclaimer */}

        <section className="legal-card">

          <div className="legal-title">

            <Shield size={26} />

            <h2>24. Privacy Disclaimer</h2>

          </div>

          <p>

            While Civilink follows reasonable security practices
            to protect personal information, users acknowledge
            that no method of electronic transmission,
            internet communication or electronic storage can
            be guaranteed to be completely secure.

          </p>

          <p>

            Users are responsible for maintaining the
            confidentiality of their account credentials and
            should immediately notify Civilink of any
            unauthorized access or suspected security incident.

          </p>

        </section>

        {/* Policy Updates */}

        <section className="legal-card">

          <div className="legal-title">

            <Database size={26} />

            <h2>25. Changes to this Privacy Policy</h2>

          </div>

          <p>

            Civilink may update this Privacy Policy from time
            to time to reflect improvements to our Platform,
            legal requirements or operational changes.

          </p>

          <p>

            The updated version will be published on the Platform
            together with the revised "Last Updated" date.

          </p>

          <p>

            Continued use of the Platform after changes become
            effective constitutes acceptance of the updated
            Privacy Policy.

          </p>

        </section>

        {/* Contact */}

        <section className="legal-card">

          <div className="legal-title">

            <Building2 size={26} />

            <h2>26. Contact Information</h2>

          </div>

          <p>

            If you have any questions, concerns or requests
            regarding this Privacy Policy or the processing
            of your personal information, please contact us.

          </p>

          <div className="contact-grid">

            <a
              className="contact-btn"
              href="https://www.civilink.in"
              target="_blank"
              rel="noreferrer"
            >
              <strong>Website</strong>
              <span>www.civilink.in</span>
            </a>

            <a
              className="contact-btn"
              href="mailto:civilink.official@gmail.com"
            >
              <strong>Email</strong>
              <span>civilink.official@gmail.com</span>
            </a>

            <a
              className="contact-btn"
              href="https://wa.me/919344425095"
              target="_blank"
              rel="noreferrer"
            >
              <strong>WhatsApp</strong>
              <span>+91 93444 25095</span>
            </a>

          </div>

        </section>

        {/* Footer */}

        <footer className="about-footer">

          <h3>Civilink Private Limited</h3>

          <p className="footer-tagline">

            Building India's Trusted Digital Construction Ecosystem

          </p>

          <p className="footer-text">

            Your privacy is important to us.

            <br />

            We are committed to protecting your personal
            information while providing a secure and trusted
            construction marketplace.

          </p>

          <div className="footer-divider"></div>

          <p className="footer-version">

            Privacy Policy Version 1.0.0

          </p>

          <p className="footer-copy">

            © 2026 Civilink Private Limited

            <br />

            All Rights Reserved.

          </p>

        </footer>

      </div>

    </>
  );
}